import { useEffect, useMemo, useRef, useState } from "react";

import {
  grants as api,
  type GrantPermission,
  type Group,
  type TenantUser,
  type UserGrant,
} from "../lib/api.js";
import { groupPaths } from "../lib/device-tree.js";
import { countLabel } from "../lib/share-label.js";

/**
 * One user's access to parts of the tree, as a count with a panel behind it.
 *
 * The count is on the row because the case it exists to catch is invisible
 * otherwise: a `member` with no grants is a working login that can see
 * nothing, which looks like a broken account to the person signing in and
 * like a perfectly normal row to the admin who created it.
 */

const PERMISSION_HELP: Record<GrantPermission, string> = {
  read: "see its devices and their history",
  write: "edit its devices' settings",
  control: "operate its devices, e.g. switch relays",
  admin: "all of the above, plus grant access",
};

const ALL: GrantPermission[] = ["read", "write", "control", "admin"];

function roleNote(role: TenantUser["role"]): string {
  switch (role) {
    case "owner":
    case "admin":
      return "Full access by role, so grants don't apply.";
    case "viewer":
      return "Can read everything. Grants add more access to part of the tree.";
    case "member":
      return "Sees only what's granted below. A grant covers everything beneath it.";
  }
}

export function AccessCell({
  user,
  isSelf,
  groups,
  onChange,
}: {
  user: TenantUser;
  isSelf: boolean;
  groups: Group[];
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<UserGrant[] | null>(null);
  const [groupId, setGroupId] = useState("");
  const [permission, setPermission] = useState<GrantPermission>("read");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  const paths = useMemo(() => groupPaths(groups), [groups]);
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => (paths.get(a.id) ?? "").localeCompare(paths.get(b.id) ?? "")),
    [groups, paths],
  );

  const fullByRole = user.role === "owner" || user.role === "admin";
  // A viewer already reads everything, so offering read would only produce a
  // grant the server refuses as meaningless.
  const offered = user.role === "viewer" ? ALL.filter((p) => p !== "read") : ALL;
  const canEdit = !isSelf && !fullByRole;

  const load = () => {
    api
      .list(user.id)
      .then((r) => setList(r.grants))
      .catch((err) => setError(err instanceof Error ? err.message : "Could not load"));
  };

  useEffect(() => {
    if (open && list === null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!offered.includes(permission)) setPermission(offered[0]!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);

  // Same dismissal as the share panel: an overlay that cannot be closed
  // without choosing something traps someone who only wanted to look.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (!cellRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const add = async () => {
    if (!groupId) return;
    setBusy(true);
    setError(null);
    try {
      await api.add(user.id, groupId, permission);
      setGroupId("");
      load();
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not grant");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (grant: UserGrant) => {
    const where = paths.get(grant.group_id) ?? grant.group_name;
    if (!confirm(`Remove ${grant.permission.toUpperCase()} on ${where} from ${user.email}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.revoke(user.id, grant.id);
      load();
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not revoke");
    } finally {
      setBusy(false);
    }
  };

  if (fullByRole) {
    return <span style={{ color: "var(--text-muted)", fontSize: 11 }}>ALL, BY ROLE</span>;
  }

  const noAccess = user.role === "member" && user.grant_count === 0;

  return (
    <div ref={cellRef} style={{ position: "relative", fontSize: 11 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          ...smallButton,
          padding: "2px 6px",
          // Padded label; see share-label.ts. nowrap would collapse the pad.
          whiteSpace: "pre",
          color: noAccess
            ? "var(--status-warning)"
            : user.grant_count > 0
              ? "var(--text-primary)"
              : "var(--text-secondary)",
          borderColor: noAccess
            ? "var(--status-warning)"
            : user.grant_count > 0
              ? "var(--border-bright)"
              : "var(--border)",
        }}
      >
        [ {countLabel("ACCESS", user.grant_count)} ]
      </button>
      {noAccess && (
        <span style={{ color: "var(--status-warning)", marginLeft: 6 }} title="A member with no grants can sign in but sees nothing">
          NO ACCESS
        </span>
      )}

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 20,
            width: 420,
            border: "1px solid var(--border-bright)",
            background: "var(--surface-raised)",
            padding: 12,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.6)",
            textAlign: "left",
            whiteSpace: "normal",
          }}
        >
          <div style={{ color: "var(--text-muted)", letterSpacing: "0.1em", marginBottom: 6 }}>
            {user.email.toUpperCase()} — ACCESS
          </div>
          <p style={{ color: "var(--text-muted)", margin: "0 0 10px", lineHeight: 1.5 }}>
            {roleNote(user.role)}
          </p>

          {error && <p style={{ color: "var(--status-critical)", margin: "0 0 8px" }}>{`!! ${error}`}</p>}
          {list === null && !error && <div style={{ color: "var(--text-muted)" }}>…</div>}
          {list !== null && list.length === 0 && (
            <div style={{ color: "var(--text-muted)", marginBottom: 8 }}>-- NO GRANTS --</div>
          )}

          {(list ?? []).map((grant) => (
            <div
              key={grant.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                padding: "4px 0",
                borderBottom: "1px solid var(--gridline)",
              }}
            >
              <span style={{ color: "var(--text-secondary)", lineHeight: 1.4 }}>
                {paths.get(grant.group_id) ?? grant.group_name}
                <span style={{ color: "var(--text-primary)" }}> · {grant.permission.toUpperCase()}</span>
                {grant.granted_by_email && (
                  <span style={{ display: "block", color: "var(--text-muted)", fontSize: 10 }}>
                    granted by {grant.granted_by_email}
                  </span>
                )}
              </span>
              {canEdit && (
                <button
                  disabled={busy}
                  onClick={() => void revoke(grant)}
                  title="Remove this grant"
                  style={{ ...smallButton, padding: "1px 6px", color: "var(--status-critical)" }}
                >
                  x
                </button>
              )}
            </div>
          ))}

          {isSelf && (
            <p style={{ color: "var(--text-muted)", margin: "10px 0 0" }}>
              You cannot change your own access.
            </p>
          )}

          {canEdit && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--gridline)" }}>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                <select value={groupId} onChange={(e) => setGroupId(e.target.value)} style={{ ...select, maxWidth: 220 }}>
                  <option value="">-- GROUP --</option>
                  {sortedGroups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {paths.get(g.id)}
                    </option>
                  ))}
                </select>
                <select
                  value={permission}
                  onChange={(e) => setPermission(e.target.value as GrantPermission)}
                  style={select}
                >
                  {offered.map((p) => (
                    <option key={p} value={p}>{p.toUpperCase()}</option>
                  ))}
                </select>
                <button disabled={!groupId || busy} onClick={() => void add()} style={smallButton}>
                  [ GRANT ]
                </button>
              </div>
              <p style={{ color: "var(--text-muted)", margin: "6px 0 0", lineHeight: 1.5 }}>
                {permission.toUpperCase()}: {PERMISSION_HELP[permission]}
                {groupId ? ` — ${paths.get(groupId)} and below.` : "."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const select: React.CSSProperties = {
  padding: "5px 7px", fontSize: 11, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)", letterSpacing: "0.06em",
};
const smallButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 9px",
  fontSize: 11, cursor: "pointer", letterSpacing: "0.08em", color: "var(--text-secondary)",
};
